<template>
  <div id="chart-fuel-consumption">
    <highcharts :options="chartOptions" :callback="onChartLoad"></highcharts>
  </div>
</template>

<script>
import { Chart } from "highcharts-vue";
import Highcharts from "highcharts";
import WindbarbHighcharts from "highcharts/modules/windbarb";
WindbarbHighcharts(Highcharts);
import noData from "highcharts/modules/no-data-to-display";
noData(Highcharts);
import { ColorMixin } from "@/mixins";
import moment from "moment-timezone";
import { createNamespacedHelpers } from "vuex";
const { mapActions, mapGetters, mapState } = createNamespacedHelpers(
  "fuelConsumption"
);

export default {
  mixins: [ColorMixin],
  components: {
    highcharts: Chart,
  },
  computed: {
    ...mapState(["chartData"]),
  },
  watch: {
    chartData: function(newValue, oldValue) {
      this.crateFuelConsumptionChartData(newValue);
    },
  },
  methods: {
    ...mapActions(["setGraphSyncLine"]),

    onChartLoad(chart) {
      this.chart = chart;
    },

    print() {
      this.chart.setSize(1000, 500, false);
    },

    crateFuelConsumptionChartData(chartData) {
      chartData.forEach(el => {
        let timeJst = moment(el.start_time).local();
        this.xCategories.push(timeJst.format("YYYY/MM/DD HH:mm"));

        //燃料
        let main =
          el.main_engine_fuel_consumption === 0
            ? 0
            : parseFloat(el.main_engine_fuel_consumption.toFixed(1));
        this.fuelConsumption.main.push(main);

        let generator =
          el.generator_fuel_consumption === 0
            ? 0
            : parseFloat(el.generator_fuel_consumption.toFixed(1));
        this.fuelConsumption.generator.push(parseFloat(generator));
        let boiler =
          el.boiler_fuel_consumption === 0
            ? 0
            : parseFloat(el.boiler_fuel_consumption.toFixed(1));
        this.fuelConsumption.boiler.push(parseFloat(boiler));

        // 風速風向
        let windVale = [];
        let windSpeed =
          el.wind_speed === 0 ? 0 : parseFloat(el.wind_speed.toFixed(1));
        windVale[0] = windSpeed;
        windVale[1] = el.wind_direction;
        this.wind.push(windVale);

        // 対水速度
        let logSpeed =
          el.average_speed_through_water === null
            ? 0
            : parseFloat(el.average_speed_through_water.toFixed(2));
        this.logSpeed.push(logSpeed);
      });

      this.chart.xAxis[0].setCategories(this.xCategories);
      this.chart.xAxis[1].setCategories(this.xCategories);
      this.chart.series[0].setData(this.fuelConsumption.main);
      this.chart.series[1].setData(this.fuelConsumption.generator);
      this.chart.series[2].setData(this.fuelConsumption.boiler);
      this.chart.series[3].setData(this.wind);
      this.chart.series[4].setData(this.logSpeed);
    },
  },

  created() {},
  mounted() {},
  data() {
    return {
      beforePrintSize: {
        width: 0,
        height: 0,
      },
      chart: null,
      xCategories: [],
      fuelConsumption: {
        main: [],
        generator: [],
        boiler: [],
      },
      wind: [],
      logSpeed: [],
      chartOptions: {
        lang: {
          noData: "表示するデータがありません",
        },
        noData: {
          style: {
            fontWeight: "bold",
            fontSize: "15px",
            color: "#303030",
          },
        },
        time: {
          getTimezoneOffset: function(timestamp) {
            const zone = "Asia/Tokyo",
              timezoneOffset = -moment.tz(timestamp, zone).utcOffset();
            return timezoneOffset;
          },
        },
        chart: {
          marginBottom: 140,
          marginLeft: 50,
          marginRight: 50,
          marginTop: 50,
          plotBorderWidth: 1,
          spacingBottom: 1,
          spacingTop: 10,
          spacingLeft: 10,
          spacingRight: 10,
          height: 460,
          alignTicks: false,
          scrollablePlotArea: {
            minWidth: 720,
          },
        },
        title: {
          text: null,
        },
        credits: {
          enabled: false,
        },
        legend: {
          itemStyle: {
            fontSize: "1.25em",
          },
          align: "center",
          verticalAlign: "bottom",
          x: 0,
          y: 0,
          borderRadius: 5,
          borderWidth: 1,
        },
        tooltip: {
          shared: true,
          useHTML: true,
          headerFormat: "<small>{point.x}</small><br><br>",
        },
        xAxis: [
          {
            categories: [],
            labels: {
              formatter: function() {
                return moment(this.value).format("HH:mm");
              },
            },
            tickWidth: 1,
            tickLength: -40,
            offset: 40,
            crosshair: true,
          },
          {
            showFirstLabel: true,
            linkedTo: 0,
            categories: [],
            tickPositioner: function() {
              if (this.categories.length === 0) return [];
              let positions = [];
              positions.push(0);
              this.categories.forEach((el, index) => {
                if (moment(el).format("HH:mm") === "00:00" && index !== 0) {
                  positions.push(index);
                }
              });
              return positions;
            },
            labels: {
              formatter: function() {
                return moment(this.value).format("MM/DD");
              },
              style: {
                color: "#333333",
                fontWeight: "bold",
              },
            },
            opposite: true,
          },
        ],
        yAxis: [
          {
            reversedStacks: false,
            title: {
              text: "L",
              offset: 0,
              align: "high",
              rotation: 0,
              style: {
                fontSize: "12px",
              },
              allowDecimals: false,
              textAlign: "right",
              x: -5,
            },
            gridLineDashStyle: "dash",
            labels: {
              format: "{value}",
              style: {
                fontSize: "12px",
              },
              y: -2,
              x: -3,
            },
            opposite: false,
            showLastLabel: false,
          },
          {
            title: {
              text: "kt",
              offset: 0,
              align: "high",
              rotation: 0,
              style: {
                fontSize: "12px",
              },
              allowDecimals: false,
              textAlign: "left",
              x: 5,
            },
            labels: {
              style: {
                fontSize: "12px",
              },
              y: 2,
              x: 3,
            },
            gridLineWidth: 0,
            opposite: true,
            showLastLabel: false,
          },
        ],
        plotOptions: {
          column: {
            stacking: "normal",
            borderRadius: 3,
            cursor: "pointer",
            events: {
              click: event => {
                this.setGraphSyncLine(event.point.index);
              },
            },
          },
          line: {
            cursor: "pointer",
            events: {
              click: event => {
                this.setGraphSyncLine(event.point.index);
              },
            },
          },
        },
        series: [
          {
            type: "column",
            name: "主機",
            color: "#008FCF",
            yAxis: 0,
            data: [],
            tooltip: {
              valueSuffix: " L",
            },
          },
          {
            type: "column",
            name: "発電機",
            color: "#60C8F6",
            yAxis: 0,
            data: [],
            tooltip: {
              valueSuffix: " L",
            },
          },
          {
            type: "column",
            name: "ボイラー",
            color: "#BDEBFF",
            yAxis: 0,
            data: [],
            tooltip: {
              valueSuffix: " L",
            },
          },
          {
            id: "windbarbs",
            type: "windbarb",
            name: "風速",
            color: "#FF8000",
            showInLegend: false,
            data: [],
            tooltip: {
              valueSuffix: " m/s",
            },
          },
          {
            type: "line",
            name: "対水速力",
            color: "#E27070",
            yAxis: 1,
            data: [],
            tooltip: {
              valueSuffix: " kt",
            },
          },
        ],
      },
    };
  },
};
</script>

<style lang="scss" scoped>
.hc {
  height: 480px;
}
</style>
